// Canonical list of supported UI locales for apps/sell and apps/shop.
// Kept in sync with core.translation_settings.supported_locales.

export const SUPPORTED_LOCALES = [
  'en',
  'es',
  'zh-CN',
  'zh-TW',
  'ja',
  'ko',
  'vi',
  'fr',
  'de',
  'pt-BR',
  'ar',
  'hi',
  'ru',
  'it',
  'th',
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = 'en';

export const RTL_LOCALES: SupportedLocale[] = ['ar'];

/**
 * Locale display label in its own language, plus the English name in
 * parens. Rendered in the language-picker dropdown.
 */
export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: 'English',
  es: 'Español (Spanish)',
  'zh-CN': '简体中文 (Simplified Chinese)',
  'zh-TW': '繁體中文 (Traditional Chinese)',
  ja: '日本語 (Japanese)',
  ko: '한국어 (Korean)',
  vi: 'Tiếng Việt (Vietnamese)',
  fr: 'Français (French)',
  de: 'Deutsch (German)',
  'pt-BR': 'Português (Brazilian Portuguese)',
  ar: 'العربية (Arabic)',
  hi: 'हिन्दी (Hindi)',
  ru: 'Русский (Russian)',
  it: 'Italiano (Italian)',
  th: 'ภาษาไทย (Thai)',
};

export function isSupportedLocale(tag: string | null | undefined): tag is SupportedLocale {
  return tag != null && (SUPPORTED_LOCALES as readonly string[]).includes(tag);
}

/**
 * Normalize a BCP-47 tag from the browser / Accept-Language / settings
 * column into one of SUPPORTED_LOCALES. Returns DEFAULT_LOCALE if no
 * match. Handles common aliases: 'zh' -> 'zh-CN', 'pt' -> 'pt-BR',
 * 'zh-Hans-CN' -> 'zh-CN', 'zh-Hant' -> 'zh-TW'.
 */
export function normalizeLocale(raw: string | null | undefined): SupportedLocale {
  if (!raw) return DEFAULT_LOCALE;
  const lower = raw.toLowerCase();
  if (isSupportedLocale(raw)) return raw;
  // direct hits on the lowercased form
  for (const supported of SUPPORTED_LOCALES) {
    if (supported.toLowerCase() === lower) return supported;
  }
  // language-only fallback
  const lang = lower.split('-')[0]!;
  if (lang === 'zh') {
    // crude script/region inference
    if (
      lower.includes('hant') ||
      lower.includes('tw') ||
      lower.includes('hk') ||
      lower.includes('mo')
    ) {
      return 'zh-TW';
    }
    return 'zh-CN';
  }
  if (lang === 'pt') return 'pt-BR';
  for (const supported of SUPPORTED_LOCALES) {
    if (supported === lang) return supported;
    if (supported.startsWith(`${lang}-`)) return supported;
  }
  return DEFAULT_LOCALE;
}

export function isRtl(tag: string | null | undefined): boolean {
  const norm = normalizeLocale(tag ?? null);
  return RTL_LOCALES.includes(norm);
}
